import styled, { keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0;

  ${down('md')} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

export const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

export const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.3);

  ${down('md')} {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
  }
`;

export const StreamIcon = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0.75rem;
    height: 0.625rem;
    border: 2px solid white;
    border-radius: 2px;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 0.5rem solid white;
    border-top: 0.3rem solid transparent;
    border-bottom: 0.3rem solid transparent;
  }

  ${down('md')} {
    width: 1rem;
    height: 1rem;
  }
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

export const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${down('md')} {
    font-size: 1rem;
  }
`;

export const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;

  ${down('md')} {
    font-size: 0.75rem;
  }
`;

type StatusVariant = 'live' | 'upcoming' | 'replay';

const statusColors: Record<StatusVariant, string> = {
  live: '#dc2626',
  upcoming: '#e5a00d',
  replay: '#64748b',
};

export const StatusIndicator = styled.div<{ $variant: StatusVariant }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: ${({ $variant }) => statusColors[$variant]};
  font-weight: 600;
  font-size: 0.75rem;
`;

export const LiveDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  background: #dc2626;
  border-radius: 50%;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

export const Separator = styled.span`
  color: #94a3b8;
`;

export const ViewersCount = styled.span`
  color: #64748b;
`;

export const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  justify-content: flex-end;

  ${down('md')} {
    width: 100%;
    overflow: visible;
  }
`;

export const TheaterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  flex-shrink: 0;
  background: #f1f5f9;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }

  svg {
    flex-shrink: 0;
  }

  ${down('md')} {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
  }
`;
